/**
 * Запасы.
 *
 * Вопросы: сколько денег лежит на складах; сколько из них не двигается
 * полгода и дольше; на каком складе; какие позиции распродавать первыми.
 *
 * Два признака залежавшегося товара, и оба названы:
 *  — «без движения N дней» — из даты последнего движения в снимке 1С,
 *    а пока 1С её не присылает — из последней продажи или поступления
 *    позиции на этом складе (считает сервер, витрина mart_stock_current);
 *  — «без продаж за 6 месяцев» — из продаж: позиция есть на складе, а
 *    реализаций по ней за полгода нет. Перемещения и списания он не видит.
 *
 * Запас — только товарные склады. Товары в пути, основные средства и прочие
 * нетоварные склады (брак, хозинвентарь) называются отдельной строкой: вид
 * склада задаётся правилами в админке.
 *
 * Количество не складывается: штуки, листы и метры. Все итоги — в рублях.
 */

import { useState, type CSSProperties } from 'react'
import { num, useReport } from '../../finance/data'
import { SegmentBar } from '../../finance/charts'
import { Caveat, DataNotes, Kpi, Panel, Segmented, SkeletonRows } from '../../finance/parts'
import { originFrom, type DrawerOrigin } from '../../finance/Drawer'
import { usePublishFreshness } from '../../finance/freshness'
import { monthLabel } from '../../finance/period'
import { money } from '../../finance/sections/Overview'
import { formatNumber, formatPercent, pluralize } from '../../format'
import { StackRows, ValueLegend } from '../charts'
import { labelOr } from '../parts'
import { AGES, FROM_1C, NO_DATE, NO_MOVES, WAREHOUSE_KINDS, ageKey, coverageOf, lastSalesMonth, monthEnd, shiftMonth, useStockCurrent, useStockMonths } from './queries'
import type { Segment } from '../../finance/charts'
import { useFinanceLessAccess } from './access'

export interface WarehouseTarget {
  value: string
  label: string
}

interface Line {
  warehouse: string
  warehouseKey: string
  nomenclature: string
  nomenclatureKey: string
  amount: number
  reserved: number
  days: number | null
  age: string
  kind: string
  /** Откуда дата движения: 1С, документы или «движений не было». */
  source: string
}

function linesOf(rows: Record<string, unknown>[] | undefined): Line[] {
  return (rows ?? [])
    .filter((row) => num(row.amount) > 0)
    .map((row) => ({
      warehouse: labelOr(row.warehouse, 'Склад не указан'),
      warehouseKey: String(row.warehouse_guid ?? ''),
      nomenclature: labelOr(row.nomenclature, 'Позиция не указана'),
      nomenclatureKey: String(row.nomenclature_guid ?? ''),
      amount: num(row.amount),
      reserved: num(row.reserved_qty),
      days: row.days_without_move === null || row.days_without_move === undefined ? null : Number(row.days_without_move),
      age: ageKey(row.days_without_move),
      // Старый сервер вида склада не знает: тогда всё — товарный запас.
      kind: String(row.warehouse_kind ?? WAREHOUSE_KINDS.goods),
      source: String(row.move_date_source ?? FROM_1C),
    }))
}

/** «> 1 150 дн.», если позиция не двигалась за всю историю: это нижняя граница. */
function daysText(line: Line): string {
  if (line.days === null) return '—'
  return `${line.source === NO_MOVES ? '> ' : ''}${formatNumber(line.days)} дн.`
}

const sumOf = (lines: Line[]) => lines.reduce((sum, line) => sum + line.amount, 0)

const AGE_ITEMS = [...AGES, NO_DATE]

function segmentsOf(lines: Line[], withDates: boolean): Segment[] {
  if (!withDates) return [{ key: 'all', label: 'Запас', value: lines.reduce((sum, line) => sum + line.amount, 0), color: 'var(--series-1)' }]
  return AGE_ITEMS.map((age) => ({
    key: age.key,
    label: age.label,
    color: age.color,
    value: lines.filter((line) => line.age === age.key).reduce((sum, line) => sum + line.amount, 0),
  })).filter((segment) => segment.value > 0)
}

/** Продажи по позициям за 6 месяцев, которыми кончаются загруженные продажи. */
function useSoldItems() {
  const access = useFinanceLessAccess()
  const months = useStockMonths(access.turnover)
  const last = lastSalesMonth(coverageOf(months))
  const window = last ? { from: shiftMonth(last, -5), to: monthEnd(last) } : null
  const sold = useReport('stock_turnover', window ? { period: window, group_by: ['nomenclature'], metrics: ['revenue'] } : null)
  const keys = new Set((sold.data?.rows ?? []).filter((row) => num(row.revenue) > 0).map((row) => String(row.nomenclature_guid ?? '')))
  return { months, sold, window, keys, ready: sold.status === 'ready', available: access.turnover }
}

export function Stock({ onOpenWarehouse }: { onOpenWarehouse: (target: WarehouseTarget, origin: DrawerOrigin) => void }) {
  const current = useStockCurrent()
  const { sold, window, keys, ready: soldReady, available: turnoverAvailable, months } = useSoldItems()
  usePublishFreshness([current])
  const [listMode, setListMode] = useState<'age' | 'unsold'>('age')

  const everything = linesOf(current.data?.rows)
  const lines = everything.filter((line) => line.kind === WAREHOUSE_KINDS.goods)
  const transitSum = sumOf(everything.filter((line) => line.kind === WAREHOUSE_KINDS.transit))
  const assetsSum = sumOf(everything.filter((line) => line.kind === WAREHOUSE_KINDS.assets))
  const otherSum = sumOf(everything.filter((line) => line.kind === WAREHOUSE_KINDS.other))
  const total = lines.reduce((sum, line) => sum + line.amount, 0)
  const withDates = lines.some((line) => line.days !== null)
  // Дата движения своя у 1С или посчитана нами по документам.
  const fromDocuments = lines.some((line) => line.days !== null && line.source !== FROM_1C)
  const positions = new Set(lines.map((line) => line.nomenclatureKey)).size
  const warehouses = new Map<string, Line[]>()
  for (const line of lines) warehouses.set(line.warehouseKey, [...(warehouses.get(line.warehouseKey) ?? []), line])

  const over180 = lines.filter((line) => line.days !== null && line.days > 180)
  const over180Sum = over180.reduce((sum, line) => sum + line.amount, 0)
  const over365Sum = lines.filter((line) => line.days !== null && line.days > 365).reduce((sum, line) => sum + line.amount, 0)
  const unsold = soldReady ? lines.filter((line) => !keys.has(line.nomenclatureKey)) : []
  const unsoldSum = unsold.reduce((sum, line) => sum + line.amount, 0)
  const unsoldPositions = new Set(unsold.map((line) => line.nomenclatureKey)).size
  const windowLabel = window ? `${monthLabel(window.from)} — ${monthLabel(window.to.slice(0, 7) + '-01')}` : ''

  const unsoldState = !turnoverAvailable
    ? ('forbidden' as const)
    : months.status !== 'ready'
      ? months.status
      : !window
        ? current.status
        : sold.status === 'ready'
          ? current.status
          : sold.status

  const mode = withDates ? listMode : 'unsold'
  const list = (mode === 'age' ? over180 : unsold).slice().sort((a, b) => b.amount - a.amount)
  const segments = segmentsOf(lines, withDates)
  const limited = current.data?.meta.limited ?? false

  return (
    <div className="fin-stack insights">
      <div className="kpi-grid">
        <Kpi
          index={0}
          state={current.status}
          label="Стоимость запасов"
          value={total}
          format={money}
          memoryKey="stk-total"
          hint={`${pluralize(positions, 'позиция', 'позиции', 'позиций')} на ${pluralize(warehouses.size, 'складе', 'складах', 'складах')}`}
        />
        <Kpi
          index={1}
          state={current.status}
          label="Без движения свыше 180 дней"
          value={withDates ? over180Sum : null}
          format={money}
          memoryKey="stk-180"
          tone={total > 0 && over180Sum / total > 0.25 ? 'bad' : undefined}
          hint={
            !withDates
              ? '1С не присылает дату последнего движения — возраст запаса не посчитать'
              : total > 0
                ? `${formatPercent((over180Sum / total) * 100)} запаса · ${pluralize(new Set(over180.map((line) => line.nomenclatureKey)).size, 'позиция', 'позиции', 'позиций')}`
                : undefined
          }
        />
        <Kpi
          index={2}
          state={current.status}
          label="Лежит больше года"
          value={withDates ? over365Sum : null}
          format={money}
          memoryKey="stk-365"
          tone={over365Sum > 0 ? 'bad' : undefined}
          hint={!withDates ? 'Нужна дата последнего движения в выгрузке остатков' : total > 0 ? `${formatPercent((over365Sum / total) * 100)} стоимости запасов` : undefined}
        />
        <Kpi
          index={3}
          state={unsoldState}
          closedText="Нужен отчёт «Оборачиваемость и рентабельность запасов»"
          label="Без продаж за 6 месяцев"
          value={window ? unsoldSum : null}
          format={money}
          memoryKey="stk-unsold"
          tone={total > 0 && unsoldSum / total > 0.25 ? 'bad' : undefined}
          hint={window ? `${pluralize(unsoldPositions, 'позиция', 'позиции', 'позиций')} · продажи ${windowLabel}` : 'Продаж в загруженных данных нет — сравнивать не с чем'}
        />
      </div>

      {(transitSum > 0 || assetsSum > 0 || otherSum > 0) && (
        <Caveat>
          В запас выше не входят:{' '}
          {[
            transitSum > 0 && `товары в пути — ${money(transitSum)}`,
            assetsSum > 0 && `основные средства — ${money(assetsSum)}`,
            otherSum > 0 && `прочие нетоварные склады (брак, хозинвентарь, имущество) — ${money(otherSum)}`,
          ].filter(Boolean).join('; ')}
          . Что считать товарным складом, задаётся в админке, в правилах «Вид склада».
        </Caveat>
      )}

      {limited && (
        <Caveat tone="warn">
          Строк остатков больше, чем выдаёт отчёт за раз, — итоги посчитаны по выданной части. Нужна серверная сводка по складам.
        </Caveat>
      )}

      <div className="fin-grid fin-grid--main">
        <Panel
          index={1}
          title="Запасы по складам"
          subtitle={withDates ? 'Длина — сумма запаса, цвет — сколько дней без движения · строка открывает склад' : 'Сумма запаса по складам · строка открывает склад'}
          states={[current]}
          skeleton={<SkeletonRows rows={4} height={18} />}
          empty={{ when: lines.length === 0, title: 'Остатков нет', hint: 'На дату снимка склады пусты, или остатки ещё не загружались.' }}
        >
          {() => (
            <StackRows
              ariaLabel="Запасы по складам"
              format={(value) => money(value)}
              rows={[...warehouses.entries()]
                .map(([key, items]) => ({ key, items, sum: items.reduce((acc, line) => acc + line.amount, 0) }))
                .sort((a, b) => b.sum - a.sum)
                .map(({ key, items, sum }) => {
                  const old = items.filter((line) => line.days !== null && line.days > 180).reduce((acc, line) => acc + line.amount, 0)
                  return {
                    key,
                    label: items[0]!.warehouse,
                    sub: withDates
                      ? `${formatPercent(total > 0 ? (sum / total) * 100 : 0)} · свыше 180 дн. — ${money(old)}`
                      : `${formatPercent(total > 0 ? (sum / total) * 100 : 0)} запаса · ${pluralize(new Set(items.map((line) => line.nomenclatureKey)).size, 'позиция', 'позиции', 'позиций')}`,
                    segments: segmentsOf(items, withDates),
                  }
                })}
              onSelect={(row, element) => onOpenWarehouse({ value: row.key, label: row.label }, originFrom(element))}
            />
          )}
        </Panel>

        <Panel
          index={2}
          title="Возраст запасов"
          subtitle={fromDocuments ? 'Сколько дней позиция лежит без продаж и поступлений на этом складе' : 'Сколько дней позиция лежит на складе без движения'}
          states={[current]}
          skeleton={<SkeletonRows rows={4} height={14} />}
          empty={
            withDates
              ? { when: lines.length === 0, title: 'Остатков нет' }
              : {
                  when: true,
                  title: 'Дата последнего движения не приходит',
                  hint: '1С не присылает дату последнего движения по остаткам, поэтому возраст запаса посчитать нельзя. Залежавшийся товар ниже определён по продажам.',
                }
          }
        >
          {() => (
            <div className="stack" style={{ gap: 14 }}>
              <SegmentBar ariaLabel="Структура запасов по возрасту" segments={segments} />
              <ul className="ageing-legend">
                {AGE_ITEMS.map((age) => {
                  const value = lines.filter((line) => line.age === age.key).reduce((sum, line) => sum + line.amount, 0)
                  if (value === 0 && age.key === NO_DATE.key) return null
                  return (
                    <li key={age.key}>
                      <span className="chart-legend__swatch" style={{ background: age.color }} aria-hidden="true" />
                      <span className="secondary">{age.label}</span>
                      <span className="mono strong">
                        {money(value)} <span className="muted ageing-legend__share">· {total > 0 ? formatPercent((value / total) * 100) : '—'}</span>
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </Panel>
      </div>

      <Panel
        index={3}
        title="Что распродавать первым"
        subtitle={
          mode === 'age'
            ? 'Без движения свыше 180 дней, по убыванию суммы'
            : `Есть на складе, продаж нет: ${windowLabel || 'продаж в данных нет'} · по убыванию суммы`
        }
        states={mode === 'age' ? [current] : [current, sold]}
        skeleton={<SkeletonRows rows={6} height={14} />}
        empty={mode === 'unsold' && (!turnoverAvailable || !window) ? {
          when: true,
          title: 'Залежавшийся товар определить нечем',
          hint: !turnoverAvailable
            ? '1С не присылает дату последнего движения, а продаж по позициям в ваших правах нет (отчёт «Оборачиваемость и рентабельность запасов»).'
            : '1С не присылает дату последнего движения, а продаж в загруженных данных нет.',
        } : {
          when: list.length === 0,
          title: mode === 'age' ? 'Залежавшегося товара нет' : 'Все позиции на складе продавались',
          hint: mode === 'age' ? 'Ни одна позиция не лежит без движения дольше 180 дней.' : `За ${windowLabel} по каждой позиции с остатком были продажи.`,
        }}
        actions={
          withDates && turnoverAvailable ? (
            <Segmented
              size="sm"
              label="Признак"
              value={listMode}
              onChange={setListMode}
              options={[
                { value: 'age', label: 'Без движения' },
                { value: 'unsold', label: 'Без продаж' },
              ]}
            />
          ) : undefined
        }
      >
        {() => (
          <>
            <div className="table-wrap">
              <table className="fin-table stock-table">
                <thead>
                  <tr>
                    <th>Позиция</th>
                    <th>Склад</th>
                    <th className="num">Сумма</th>
                    {withDates && <th className="num">Без движения</th>}
                    {soldReady && <th>Продажи за 6 мес.</th>}
                  </tr>
                </thead>
                <tbody>
                  {list.slice(0, 10).map((line, index) => (
                    <tr key={`${line.warehouseKey}|${line.nomenclatureKey}`} className="cascade" style={{ '--i': Math.min(index, 7) } as CSSProperties}>
                      <td className="stock-name" title={line.nomenclature}>
                        {line.nomenclature}
                        <span className="stock-sub">{line.warehouse}</span>
                      </td>
                      <td className="secondary">{line.warehouse}</td>
                      <td className="num mono strong">{money(line.amount)}</td>
                      {withDates && <td className="num mono">{daysText(line)}</td>}
                      {soldReady && (
                        <td>{keys.has(line.nomenclatureKey) ? <span className="secondary">есть</span> : <span className="stock-flag">нет продаж</span>}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {list.length > 10 && (
              <div className="tiny muted" style={{ marginTop: 8 }}>
                Показаны 10 крупнейших из {list.length}; ещё {money(list.slice(10).reduce((sum, line) => sum + line.amount, 0))}. Полный список — в отчёте «Остатки и неликвиды».
              </div>
            )}
          </>
        )}
      </Panel>

      <Caveat>
        Количество на складах не суммируется: штуки, листы и метры — разные единицы. Все итоги — сумма остатка из 1С в рублях.
      </Caveat>
      {fromDocuments && (
        <p className="fin-footnote">
          «Без движения» считается от последней продажи или последнего поступления позиции на этом складе: дату движения
          1С пока не присылает. Перемещений и списаний в выгрузке нет, поэтому товар, недавно перемещённый с другого склада,
          здесь выглядит старше, чем есть. Если позиция не продавалась и не поступала за всю загруженную историю, срок
          показан как «больше N дней» — от первого загруженного документа.
        </p>
      )}
      {withDates && !fromDocuments && (
        <p className="fin-footnote">
          «Без движения» считается от даты последнего движения, которую 1С передаёт вместе с остатками: это последний
          документ, изменивший остаток позиции на этом складе (продажа, поступление, перемещение, списание). Если по позиции
          давно не было ни одного такого документа, деньги в ней лежат без оборота.
        </p>
      )}
      <DataNotes states={[current, sold]} />
    </div>
  )
}

// --- Склад в детализации -----------------------------------------------------

export function WarehouseDrill({ target }: { target: WarehouseTarget }) {
  const current = useStockCurrent()
  const lines = linesOf(current.data?.rows).filter((line) => line.warehouseKey === target.value)
  const fromDocuments = lines.some((line) => line.days !== null && line.source !== FROM_1C)
  const total = lines.reduce((sum, line) => sum + line.amount, 0)
  const withDates = lines.some((line) => line.days !== null)
  const top = [...lines].sort((a, b) => b.amount - a.amount)

  return (
    <div className="drill-step stack" style={{ gap: 18 }}>
      <div className="drill-kpis">
        {[
          { label: 'Запас на складе', text: money(total) },
          { label: 'Позиций', text: String(lines.length) },
          ...(withDates
            ? [{ label: 'Свыше 180 дней', text: money(lines.filter((line) => line.days !== null && line.days > 180).reduce((sum, line) => sum + line.amount, 0)) }]
            : []),
        ].map((item, index) => (
          <div key={item.label} className="drill-kpi cascade" style={{ '--i': Math.min(index, 7) } as CSSProperties}>
            <div className="tiny muted">{item.label}</div>
            <div className="drill-kpi__value mono">{item.text}</div>
          </div>
        ))}
      </div>
      {withDates && (
        <div className="stack" style={{ gap: 10 }}>
          <SegmentBar ariaLabel="Возраст запаса на складе" segments={segmentsOf(lines, true)} />
          <ValueLegend total={total} items={segmentsOf(lines, true).map((segment) => ({ key: segment.key, label: segment.label, color: segment.color, value: segment.value }))} />
        </div>
      )}
      <div className="table-wrap">
        <table className="fin-table">
          <thead>
            <tr>
              <th>Позиция</th>
              <th className="num">Сумма</th>
              {withDates && <th className="num">Без движения</th>}
            </tr>
          </thead>
          <tbody>
            {top.slice(0, 20).map((line, index) => (
              <tr key={line.nomenclatureKey} className="cascade" style={{ '--i': Math.min(index, 7) } as CSSProperties}>
                <td className="stock-name" title={line.nomenclature}>
                  {line.nomenclature}
                </td>
                <td className="num mono">{money(line.amount)}</td>
                {withDates && <td className="num mono">{daysText(line)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {top.length > 20 && <div className="tiny muted">Показаны 20 крупнейших из {top.length}.</div>}
      {fromDocuments && <div className="tiny muted">Без движения — от последней продажи или поступления позиции на этом складе.</div>}
    </div>
  )
}
